import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiX, FiUser, FiMail, FiPhone, FiMessageSquare, FiDollarSign, FiClock } from 'react-icons/fi';

const TeamSection = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const [showCommunityTaxForm, setShowCommunityTaxForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    irsDebt: '',
    situation: '',
    contactTime: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const teamMembers = [
    {
      name: "Simeon Snow",
      position: "Wealth Management\nRegistered Representative\n(Series 7 & 63 licensed)\nTax Accountant",
      image: "https://cdn.prod.website-files.com/64d13f6ce094cf9b34b8f52f/64d3670c81f2d686ad4d46d0_simeon%20snow.jpeg"
    },
    {
      name: "Erika Snow",
      position: "Office Manager\nTax Accountant",
      image: "https://cdn.prod.website-files.com/64d13f6ce094cf9b34b8f52f/64d57759920ced9d9e45606c_ErikaBackground.png"
    },
    {
      name: "Alexis Snow",
      position: "Client Relations Manager\nTax Accountant",
      image: "https://cdn.prod.website-files.com/64d13f6ce094cf9b34b8f52f/64d577737587924015e640f0_AlexisBackground.png"
    },
    {
      name: "Geoff Levine",
      position: "Enrolled Agent\nRemote Consultant\nMS in Taxation",
      image: "https://i.postimg.cc/NfwDcFr8/GEOFF-LEVINE.png"
    }
  ];

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const formattedPhone = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, [name]: formattedPhone }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const openCommunityTaxForm = (e) => {
    e.preventDefault();
    setShowCommunityTaxForm(true);
  };

  const closeCommunityTaxForm = () => {
    setShowCommunityTaxForm(false);
    setSubmitStatus(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form-name': 'community-tax-contact',
          ...formData
        }).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          irsDebt: '',
          situation: '',
          contactTime: ''
        });
        setTimeout(() => {
          closeCommunityTaxForm();
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section id="team" className="py-20 bg-white" ref={ref}>
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Meet The Team</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Established in 2008. We are a family owned &amp; operated business, located at 144 Linwoo Drive Paragould AR.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="text-center"
              >
                <div className="relative mx-auto w-48 h-48 mb-6">
                  <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-blue-100">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-2">{member.name}</h3>
                <p className="text-gray-600 whitespace-pre-line">
                  {member.position}
                </p>
              </motion.div>
            ))}

            {/* Community Tax Contact Bubble */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="text-center"
            >
              <div className="relative mx-auto w-48 h-48 mb-6">
                <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-blue-100 bg-white flex items-center justify-center">
                  <img
                    src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1757642747003-community-tax-square-logo.png"
                    alt="Community Tax"
                    className="w-32 h-32 object-contain"
                  />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">Community Tax</h3>
              <p className="text-gray-600 mb-4">
                We partner with Attorneys, CPA's and IRS Enrolled Agents for Tax Resolution Services
              </p>
              <button
                onClick={openCommunityTaxForm}
                className="text-blue-600 hover:text-blue-800 underline font-medium transition-colors"
              >
                Click to learn more
              </button>
            </motion.div>

            {/* Tax owlchemy Labs Contact Bubble */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.0 }}
              className="text-center"
            >
              <div className="relative mx-auto w-48 h-48 mb-6">
                <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-blue-100 bg-white flex items-center justify-center">
                  <img
                    src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1757647892739-white%20background%20not%20transparent.png"
                    alt="Tax owlchemy Labs"
                    className="w-40 h-40 object-contain"
                  />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">Tax Owlchemy Labs</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                *Founded by Simeon Snow<br />
                *Full Suite of Software Solutions for Tax Professionals<br />
                *IRS Approved CPE Provider<br />
                *Brand Marketing &amp; Compliance Assets<br />
                *More Info Coming Soon!
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Community Tax Contact Form Modal */}
      <AnimatePresence>
        {showCommunityTaxForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={closeCommunityTaxForm}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-blue-50">
                <div className="flex items-center">
                  <img
                    src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1757642747003-community-tax-square-logo.png"
                    alt="Community Tax"
                    className="w-12 h-12 mr-4"
                  />
                  <h2 className="text-2xl font-bold text-gray-900">Tax Resolution Services</h2>
                </div>
                <button onClick={closeCommunityTaxForm} className="text-gray-400 hover:text-gray-600">
                  <FiX size={24} />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                {/* Partnership Information */}
                <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-blue-900 mb-3">
                    We partner with one of the largest Tax Resolution Teams in the Country:
                  </h3>
                  <ul className="text-blue-800 space-y-2">
                    <li>• 4.7 star rating on The Credit Review</li>
                    <li>• Consulted with over 110k clients</li>
                    <li>• Over $1.3 billion in tax liability resolved</li>
                    <li>• Licensed Tax Practitioners, Attorneys, CPA's and IRS Enrolled Agents on Staff</li>
                    <li>• Tax Debt Settlement &amp; Complete Tax Resolution Services</li>
                  </ul>
                </div>

                {/* Instructions */}
                <div className="mb-6">
                  <p className="text-gray-700 text-center">
                    Input your contact details below &amp; we will reach out to you for a complimentary consultation to see if these services can help you.
                  </p>
                </div>

                {/* Success/Error Messages */}
                {submitStatus === 'success' && (
                  <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg text-center">
                    <h4 className="font-semibold mb-2">Thank You!</h4>
                    <p>Your information has been submitted successfully. We will contact you soon for your complimentary consultation.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                    Sorry, there was an error submitting your information. Please try again.
                  </div>
                )}

                {/* Contact Form */}
                {submitStatus !== 'success' && (
                  <form onSubmit={handleSubmit} name="community-tax-contact" data-netlify="true" method="POST">
                    <input type="hidden" name="form-name" value="community-tax-contact" />

                    {/* Name Field */}
                    <div className="mb-4">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        <FiUser className="inline mr-1" />
                        Name: *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                        placeholder="Your full name"
                      />
                    </div>

                    {/* Phone Field */}
                    <div className="mb-4">
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        <FiPhone className="inline mr-1" />
                        Phone Number: *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                        placeholder="(555) 123-4567"
                        maxLength="14"
                      />
                    </div>

                    {/* Email Field */}
                    <div className="mb-4">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        <FiMail className="inline mr-1" />
                        Email Address: *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                        placeholder="your.email@example.com"
                      />
                    </div>

                    {/* IRS Debt Amount Field */}
                    <div className="mb-4">
                      <label htmlFor="irsDebt" className="block text-sm font-medium text-gray-700 mb-2">
                        <FiDollarSign className="inline mr-1" />
                        Amount of IRS Debt: *
                      </label>
                      <input
                        type="text"
                        id="irsDebt"
                        name="irsDebt"
                        value={formData.irsDebt}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                        placeholder="e.g., $50,000 or Approximate amount"
                      />
                    </div>

                    {/* Situation Description Field */}
                    <div className="mb-4">
                      <label htmlFor="situation" className="block text-sm font-medium text-gray-700 mb-2">
                        <FiMessageSquare className="inline mr-1" />
                        Describe your situation that you need assistance with: *
                      </label>
                      <textarea
                        id="situation"
                        name="situation"
                        value={formData.situation}
                        onChange={handleInputChange}
                        required
                        rows="4"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors resize-none"
                        placeholder="Please describe your tax situation and what assistance you need..."
                      />
                    </div>

                    {/* Best Contact Time Field */}
                    <div className="mb-6">
                      <label htmlFor="contactTime" className="block text-sm font-medium text-gray-700 mb-2">
                        <FiClock className="inline mr-1" />
                        Best available time to contact you?: *
                      </label>
                      <input
                        type="text"
                        id="contactTime"
                        name="contactTime"
                        value={formData.contactTime}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                        placeholder="e.g., Weekdays 9am-5pm, Evenings after 6pm, etc."
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Request'}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default TeamSection;